import { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { Plus, Search, X, FileUp, Download, Edit2, ImagePlus, Trash2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

const PAGE_SIZE = 200;

// Base form shape, reused on open/reset to keep all fields (incl. pack/COMPOSITE) in sync.
const EMPTY_FORM = {
    nombre: '',
    codigo_barra: '',
    valor_producto: '',
    stock_minimo: '',
    descripcion: '',
    fecha_vencimiento: '',
    sub_categoria_id: '',
    venta_por_peso: false,
    precio_por_kilo: '',
    // Pack (inventario compuesto): el pack descuenta del stock del producto unidad padre.
    inventory_type: 'SIMPLE',
    parent_product_id: '',
    conversion_factor: '',
    parent_label: '',
};

export default function ProductsPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    // Solo permitir si el permiso está explícitamente en el array (seguridad estricta)
    const canEdit = Array.isArray(user?.permissions) && user.permissions.includes('productos_editar');
    const canDelete = Array.isArray(user?.permissions) && user.permissions.includes('productos_eliminar');
    const fileInputRef = useRef(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);

    // Form State
    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [pendingPhoto, setPendingPhoto] = useState(null);

    const [categories, setCategories] = useState([]);

    // Pack (parent product) search: only SIMPLE products can be a pack's unit parent.
    const [parentQuery, setParentQuery] = useState('');
    const [parentResults, setParentResults] = useState([]);
    const [parentSearching, setParentSearching] = useState(false);
    const [showParentDropdown, setShowParentDropdown] = useState(false);
    const isPack = formData.inventory_type === 'COMPOSITE';

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setProducts([]);
            setHasMore(true);
            fetchProducts(searchTerm, 0, true);
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const fetchCategories = () => {
        apiFetch(`${API_URL}/api/categories`)
            .then(res => res.json())
            .then(setCategories)
            .catch(console.error);
    };

    // Debounced search for the unit parent product when configuring a pack.
    useEffect(() => {
        if (!isPack) return;
        const q = parentQuery.trim();
        if (q.length < 2) {
            setParentResults([]);
            return;
        }
        setParentSearching(true);
        const t = setTimeout(() => {
            const params = new URLSearchParams();
            params.set('search', q);
            params.set('limit', 20);
            apiFetch(`${API_URL}/api/products?${params}`)
                .then(res => (res.ok ? res.json() : []))
                .then(data => {
                    const list = Array.isArray(data) ? data : [];
                    // A pack parent must be a SIMPLE product (no chained packs) and not itself.
                    setParentResults(
                        list.filter(p =>
                            (p.inventory_type || 'SIMPLE') !== 'COMPOSITE' &&
                            p.id !== editingProduct?.id
                        )
                    );
                })
                .catch(() => setParentResults([]))
                .finally(() => setParentSearching(false));
        }, 300);
        return () => clearTimeout(t);
    }, [parentQuery, isPack, editingProduct]);

    const selectParentProduct = (p) => {
        setFormData(prev => ({
            ...prev,
            parent_product_id: p.id,
            parent_label: `${p.nombre}${p.codigo_barra ? ` (${p.codigo_barra})` : ''}`,
        }));
        setParentQuery('');
        setParentResults([]);
        setShowParentDropdown(false);
    };

    const togglePack = (checked) => {
        setFormData(prev => ({
            ...prev,
            inventory_type: checked ? 'COMPOSITE' : 'SIMPLE',
            // Packs cannot be sold by weight and need a default units-per-pack.
            venta_por_peso: checked ? false : prev.venta_por_peso,
            conversion_factor: checked ? (prev.conversion_factor || '6') : '',
            parent_product_id: checked ? prev.parent_product_id : '',
            parent_label: checked ? prev.parent_label : '',
        }));
        setParentQuery('');
        setParentResults([]);
    };

    const fetchProducts = (search = '', offset = 0, reset = false) => {
        if (reset) setLoading(true);
        else setLoadingMore(true);

        const params = new URLSearchParams();
        if (search) params.set('search', search);
        params.set('limit', PAGE_SIZE);
        params.set('offset', offset);

        apiFetch(`${API_URL}/api/products?${params}`)
            .then(res => {
                if (res.status === 401) {
                    logout();
                    navigate('/login');
                    setLoading(false);
                    setLoadingMore(false);
                    return null;
                }
                return res.json();
            })
            .then(data => {
                if (data === null) return;
                const sorted = [...(data || [])].sort((a, b) => {
                    const sa = a.stock_actual ?? 0;
                    const sb = b.stock_actual ?? 0;
                    if (sa <= 0 && sb > 0) return 1;
                    if (sa > 0 && sb <= 0) return -1;
                    return (a.nombre || '').localeCompare(b.nombre || '');
                });
                setProducts(prev => reset ? sorted : [...prev, ...sorted]);
                setHasMore((data || []).length >= PAGE_SIZE);
                setLoading(false);
                setLoadingMore(false);
            })
            .catch(() => {
                setLoading(false);
                setLoadingMore(false);
            });
    };

    const loadMore = useCallback(() => {
        if (loadingMore || !hasMore) return;
        fetchProducts(searchTerm, products.length, false);
    }, [searchTerm, products.length, loadingMore, hasMore]);

    const sentinelRef = useRef(null);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMore();
            },
            { root: null, rootMargin: '200px', threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [loadMore]);

    const handleDownloadTemplate = async () => {
        try {
            const res = await apiFetch(`${API_URL}/api/products/template`);
            if (!res.ok) throw new Error('Error al descargar plantilla');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'plantilla_productos.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            alert(err.message || 'Error al descargar plantilla');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const res = await apiFetch(`${API_URL}/api/products/bulk`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();

            if (res.ok) {
                const c = data.created ?? 0;
                const u = data.updated ?? 0;
                let msg = `Carga completada: ${data.success} éxitos (${c} nuevos, ${u} actualizados), ${data.failed} fallos.`;
                if (data.failed > 0 && Array.isArray(data.errors) && data.errors[0]?.error) {
                    msg += `\n\nPrimer error: ${data.errors[0].error}`;
                }
                alert(msg);
                if (data.errors?.length > 0) console.table(data.errors);
                fetchProducts(searchTerm, 0, true);
            } else {
                alert(`Error: ${data.error}`);
            }
        } catch (err) {
            console.error(err);
            alert('Error al subir el archivo');
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (isPack && !formData.parent_product_id) {
            alert('Seleccione el producto unidad del que el pack descontará stock.');
            return;
        }
        try {
            const payload = {
                ...formData,
                stock_minimo: Math.max(0, parseInt(formData.stock_minimo, 10) || 0),
                venta_por_peso: !!formData.venta_por_peso,
                precio_por_kilo: formData.venta_por_peso ? (parseFloat(formData.precio_por_kilo) || 0) : null,
                inventory_type: isPack ? 'COMPOSITE' : 'SIMPLE',
                parent_product_id: isPack ? formData.parent_product_id : null,
                conversion_factor: isPack ? Math.max(1, parseInt(formData.conversion_factor, 10) || 1) : 1,
            };
            const res = await apiFetch(`${API_URL}/api/products`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const newProduct = await res.json();
                if (pendingPhoto && newProduct.id) {
                    const fd = new FormData();
                    fd.append('photo', pendingPhoto);
                    const photoRes = await apiFetch(`${API_URL}/api/products/${newProduct.id}/photo`, {
                        method: 'PUT',
                        body: fd
                    });
                    if (photoRes.ok) {
                        setPendingPhoto(null);
                    }
                }
                setShowModal(false);
                setFormData({ ...EMPTY_FORM });
                fetchProducts(searchTerm, 0, true);
                alert('Producto creado exitosamente');
            } else {
                alert('Error al crear producto');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const imageInputRef = useRef(null);

    const handleEdit = (product) => {
        setEditingProduct(product);
        const composite = (product.inventory_type || 'SIMPLE') === 'COMPOSITE';
        setFormData({
            ...EMPTY_FORM,
            nombre: product.nombre || '',
            codigo_barra: product.codigo_barra || '',
            valor_producto: product.valor_producto ?? '',
            stock_minimo: product.stock_minimo ?? '',
            descripcion: product.descripcion || '',
            fecha_vencimiento: product.fecha_vencimiento ? product.fecha_vencimiento.split('T')[0] : '',
            sub_categoria_id: product.sub_categoria_id || '',
            venta_por_peso: !!product.venta_por_peso,
            precio_por_kilo: product.precio_por_kilo ?? '',
            inventory_type: composite ? 'COMPOSITE' : 'SIMPLE',
            parent_product_id: composite ? (product.parent_product_id ?? '') : '',
            conversion_factor: composite ? (product.conversion_factor ?? '') : '',
            parent_label: composite && product.parent_product_id ? `Producto #${product.parent_product_id}` : '',
        });
        setParentQuery('');
        setParentResults([]);
        setShowModal(true);

        // Resolve parent product name for display (the list row only carries the id).
        if (composite && product.parent_product_id) {
            apiFetch(`${API_URL}/api/products/${product.parent_product_id}`)
                .then(res => (res.ok ? res.json() : null))
                .then(parent => {
                    if (parent) {
                        setFormData(prev => prev.parent_product_id === product.parent_product_id
                            ? { ...prev, parent_label: `${parent.nombre}${parent.codigo_barra ? ` (${parent.codigo_barra})` : ''}` }
                            : prev);
                    }
                })
                .catch(() => {});
        }
    };

    const handlePhotoUpload = async (e, productId) => {
        const file = e.target.files[0];
        if (!file) return;
        const formDataUpload = new FormData();
        formDataUpload.append('photo', file);
        try {
            const res = await apiFetch(`${API_URL}/api/products/${productId}/photo`, {
                method: 'PUT',
                body: formDataUpload
            });
            if (res.ok) {
                fetchProducts(searchTerm, 0, true);
                const updated = await res.json();
                if (editingProduct?.id === productId) {
                    setEditingProduct(prev => prev ? { ...prev, foto_url: updated.foto_url } : prev);
                }
                alert('Imagen actualizada correctamente');
            } else {
                const err = await res.json();
                alert(err.error || 'Error al subir imagen');
            }
        } catch (err) {
            console.error(err);
            alert('Error al subir la imagen');
        } finally {
            if (imageInputRef.current) imageInputRef.current.value = '';
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!editingProduct) return;
        if (isPack && !formData.parent_product_id) {
            alert('Seleccione el producto unidad del que el pack descontará stock.');
            return;
        }
        try {
            const payload = {
                ...formData,
                inventory_type: isPack ? 'COMPOSITE' : 'SIMPLE',
                parent_product_id: isPack ? formData.parent_product_id : null,
                conversion_factor: isPack ? Math.max(1, parseInt(formData.conversion_factor, 10) || 1) : 1,
            };
            const res = await apiFetch(`${API_URL}/api/products/${editingProduct.id}`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                setShowModal(false);
                setEditingProduct(null);
                setFormData({ ...EMPTY_FORM });
                fetchProducts(searchTerm, 0, true);
                alert('Producto actualizado exitosamente');
            } else {
                alert('Error al actualizar producto');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (e, product) => {
        e.stopPropagation();
        if (!confirm(`¿Eliminar el producto "${product.nombre}"? Esta acción no se puede deshacer.`)) return;
        try {
            const res = await apiFetch(`${API_URL}/api/products/${product.id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setProducts(prev => prev.filter(p => p.id !== product.id));
                if (editingProduct?.id === product.id) {
                    setShowModal(false);
                    setEditingProduct(null);
                }
                alert('Producto eliminado');
            } else {
                const err = await res.json();
                alert(err.error || 'Error al eliminar');
            }
        } catch (err) {
            console.error(err);
            alert('Error al eliminar producto');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="products-page">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <div className="relative flex-1 w-full md:w-auto flex items-center border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
                    <div className="pl-3 pr-2 py-2.5 text-gray-400 flex-shrink-0">
                        <Search size={20} />
                    </div>
                    <input
                        type="text"
                        placeholder="Buscar por nombre, código o categoría..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="flex-1 min-w-0 py-2.5 pr-10 border-0 focus:ring-0 focus:outline-none bg-transparent"
                        style={{ fontSize: '0.95rem' }}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => setSearchTerm('')}
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="flex gap-3 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
                    <button
                        onClick={handleDownloadTemplate}
                        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors whitespace-nowrap shadow-sm"
                    >
                        <Download size={18} /> Plantilla
                    </button>

                    {canEdit && (
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 text-sm font-medium transition-colors whitespace-nowrap shadow-sm"
                    >
                        <FileUp size={18} /> Importar
                    </button>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                    />
                    {canEdit && (
                    <button
                        className="btn-primary flex items-center gap-2 px-4 py-2.5 shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                        onClick={() => {
                            setEditingProduct(null);
                            setFormData({ ...EMPTY_FORM });
                            setParentQuery('');
                            setParentResults([]);
                            setShowModal(true);
                        }}
                    >
                        <Plus size={20} /> Nuevo Producto
                    </button>
                    )}
                </div>
            </div>

            {loading ? (
                <LoadingSpinner message="Cargando catálogo..." />
            ) : (
                <div className="product-grid">
                    {products.map((p) => (
                        <div
                            key={p.id}
                            className={`product-card relative group ${canEdit ? 'cursor-pointer' : ''}`}
                            onClick={() => canEdit && handleEdit(p)}
                        >
                            {(canEdit || canDelete) && (
                                <div className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {canEdit && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleEdit(p); }}
                                            className="p-2 bg-white rounded-full shadow-md hover:bg-indigo-50"
                                            title="Editar producto"
                                            aria-label="Editar producto"
                                        >
                                            <Edit2 size={16} className="text-indigo-600" />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={(e) => handleDelete(e, p)}
                                            className="p-2 bg-white rounded-full shadow-md hover:bg-red-50"
                                            title="Eliminar producto"
                                            aria-label="Eliminar producto"
                                        >
                                            <Trash2 size={16} className="text-red-600" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="product-card-image">
                                {p.foto_url ? (
                                    <img src={p.foto_url.startsWith('http') ? p.foto_url : `${API_URL}${p.foto_url}`} alt={p.nombre} />
                                ) : (
                                    <div className="product-card-placeholder">Sin Imagen</div>
                                )}
                            </div>
                            <h3>{p.nombre}</h3>
                            {(p.inventory_type === 'COMPOSITE') && (
                                <span className="inline-block bg-amber-50 text-amber-800 text-xs px-2 py-0.5 rounded-full mb-1 font-medium">
                                    Pack ×{p.conversion_factor || '?'}
                                </span>
                            )}
                            {p.descripcion && (
                                <div className="text-xs text-gray-500 mb-2">{p.descripcion}</div>
                            )}
                            <div className="text-sm text-muted mb-4">{p.codigo_barra || 'Sin Código'}</div>
                            {p.subcategoria_nombre && (
                                <span className="inline-block bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded-full mb-2">
                                    {p.subcategoria_nombre}
                                </span>
                            )}
                            {p.fecha_vencimiento && (
                                <div className="text-xs text-orange-600 mb-2">
                                    Vence: {new Date(p.fecha_vencimiento).toLocaleDateString()}
                                </div>
                            )}
                            <div className="flex justify-between items-center mt-auto">
                                <div className="price">{formatCurrency(p.valor_producto)}</div>
                                <div className={`text-sm font-bold ${(p.stock_actual ?? 0) <= 0 ? 'text-red-600' : p.stock_actual <= p.stock_minimo ? 'text-yellow-600' : 'text-muted'}`}>
                                    Stock: {Math.max(0, p.stock_actual ?? 0)}
                                </div>
                            </div>
                        </div>
                    ))}
                    <div ref={sentinelRef} style={{ gridColumn: '1 / -1', height: 1 }} aria-hidden="true" />
                    {loadingMore && (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '1rem' }}>
                            <LoadingSpinner message="Cargando más productos..." />
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay">
                    <div
                        className="modal-content"
                        style={{
                            maxWidth: '560px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}
                    >
                        <button
                            onClick={() => {
                                setShowModal(false);
                                setEditingProduct(null);
                                setPendingPhoto(null);
                                setFormData({ ...EMPTY_FORM });
                                setParentQuery('');
                                setParentResults([]);
                            }}
                            className="modal-close"
                        >
                            <X size={20} />
                        </button>
                        <h2 className="modal-title">{editingProduct ? 'Editar Producto' : 'Agregar Nuevo Producto'}</h2>
                        <form onSubmit={editingProduct ? handleUpdate : handleCreate} className="form-grid">
                            <div className="form-group full-width">
                                <label>Nombre</label>
                                <input
                                    required
                                    value={formData.nombre}
                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Código de Barra</label>
                                <input
                                    value={formData.codigo_barra}
                                    onChange={e => setFormData({ ...formData, codigo_barra: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Precio{formData.venta_por_peso ? ' por kilo' : ''}</label>
                                <input
                                    type="number"
                                    required={!formData.venta_por_peso}
                                    value={formData.venta_por_peso ? formData.precio_por_kilo : formData.valor_producto}
                                    onChange={e => setFormData({
                                        ...formData,
                                        ...(formData.venta_por_peso ? { precio_por_kilo: e.target.value } : { valor_producto: e.target.value })
                                    })}
                                />
                            </div>
                            <div className="form-group flex items-end gap-2">
                                <label className={`flex items-center gap-2 ${isPack ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        disabled={isPack}
                                        checked={formData.venta_por_peso}
                                        onChange={e => setFormData({ ...formData, venta_por_peso: e.target.checked })}
                                    />
                                    Venta por peso (ej: tomates, pan, cecinas)
                                </label>
                            </div>

                            {/* Pack (inventario compuesto): un pack con código propio descuenta N unidades del stock del producto unidad. */}
                            <div className="form-group full-width">
                                <label className={`flex items-center gap-2 ${formData.venta_por_peso ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        disabled={formData.venta_por_peso}
                                        checked={isPack}
                                        onChange={e => togglePack(e.target.checked)}
                                    />
                                    Es un pack (descuenta del stock de otro producto)
                                </label>
                                <p className="text-xs text-gray-500 mt-1">
                                    Ej: un pack de 6 cervezas con su propio código de barra descuenta 6 unidades del stock de la cerveza individual.
                                </p>
                            </div>

                            {isPack && (
                                <>
                                    <div className="form-group full-width relative">
                                        <label>Producto unidad (de dónde se descuenta el stock)</label>
                                        {formData.parent_product_id ? (
                                            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50">
                                                <span className="text-sm font-medium text-indigo-800 truncate">
                                                    {formData.parent_label || `Producto #${formData.parent_product_id}`}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="text-xs text-indigo-600 hover:underline whitespace-nowrap"
                                                    onClick={() => setFormData(prev => ({ ...prev, parent_product_id: '', parent_label: '' }))}
                                                >
                                                    Cambiar
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <input
                                                    type="text"
                                                    placeholder="Buscar cerveza/unidad por nombre o código..."
                                                    value={parentQuery}
                                                    onChange={e => { setParentQuery(e.target.value); setShowParentDropdown(true); }}
                                                    onFocus={() => setShowParentDropdown(true)}
                                                />
                                                {showParentDropdown && (parentSearching || parentResults.length > 0 || parentQuery.trim().length >= 2) && (
                                                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                                                        {parentSearching && (
                                                            <div className="px-3 py-2 text-sm text-gray-500">Buscando...</div>
                                                        )}
                                                        {!parentSearching && parentResults.length === 0 && (
                                                            <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                                                        )}
                                                        {parentResults.map(p => (
                                                            <button
                                                                key={p.id}
                                                                type="button"
                                                                onClick={() => selectParentProduct(p)}
                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex justify-between gap-2"
                                                            >
                                                                <span className="truncate">{p.nombre}</span>
                                                                <span className="text-gray-400 whitespace-nowrap">{p.codigo_barra || 'sin código'}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Unidades por pack</label>
                                        <input
                                            type="number"
                                            min={2}
                                            step={1}
                                            required
                                            placeholder="6"
                                            value={formData.conversion_factor}
                                            onChange={e => setFormData({ ...formData, conversion_factor: e.target.value })}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Ej: 6, 12, 10. Al vender 1 pack se descuentan estas unidades del producto unidad.
                                        </p>
                                    </div>
                                </>
                            )}

                            {editingProduct && !isPack && (
                                <div className="form-group full-width">
                                    <label>Stock actual (inventario real)</label>
                                    <div className={`px-3 py-2 rounded-lg border font-semibold ${
                                        (editingProduct.stock_actual ?? 0) <= 0 
                                            ? 'bg-red-50 border-red-200 text-red-700' 
                                            : (editingProduct.stock_actual ?? 0) <= (editingProduct.stock_minimo ?? 0)
                                                ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                                                : 'bg-gray-50 border-gray-200 text-gray-700'
                                    }`}>
                                        {Math.max(0, editingProduct.stock_actual ?? 0)} unidades
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        El stock real proviene del inventario. Para modificarlo use Compras o Ajustes de inventario.
                                    </p>
                                </div>
                            )}
                            {isPack ? (
                                <div className="form-group full-width">
                                    <label>Stock del pack</label>
                                    <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 text-sm">
                                        El pack no tiene stock propio. Su disponibilidad se calcula como
                                        {' '}<strong>stock del producto unidad ÷ unidades por pack</strong>.
                                    </div>
                                </div>
                            ) : (
                            <div className="form-group">
                                <label>{editingProduct ? 'Stock mínimo (umbral alerta)' : 'Stock Inicial'}{formData.venta_por_peso ? ' (en gramos)' : ''}</label>
                                <input
                                    type="number"
                                    min={0}
                                    step={formData.venta_por_peso ? 100 : 1}
                                    required
                                    value={formData.stock_minimo}
                                    onChange={e => setFormData({ ...formData, stock_minimo: e.target.value })}
                                />
                                {formData.venta_por_peso && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Para productos por peso, el stock se mide en gramos (ej: 5000 = 5 kg).
                                    </p>
                                )}
                                {editingProduct && !formData.venta_por_peso && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Umbral para alertas cuando el stock real baja. No modifica el inventario.
                                    </p>
                                )}
                                {!editingProduct && !formData.venta_por_peso && (
                                    <p className="text-xs text-amber-600 mt-1.5 bg-amber-50 px-2 py-1.5 rounded border border-amber-200">
                                        Solo aplica para productos nuevos. El ingreso oficial de stock se realiza al registrar una factura de compra.
                                    </p>
                                )}
                            </div>
                            )}

                            <div className="form-group full-width">
                                <label>Fecha de Vencimiento (Opcional)</label>
                                <input
                                    type="date"
                                    value={formData.fecha_vencimiento || ''}
                                    onChange={e => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                                />
                            </div>

                            <div className="form-group full-width">
                                <label>Descripción</label>
                                <input
                                    value={formData.descripcion}
                                    onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                                />
                            </div>

                            {(editingProduct || pendingPhoto) && (
                                <div className="form-group full-width">
                                    <label>Imagen del producto</label>
                                    <div className="flex items-center gap-4">
                                        {editingProduct?.foto_url || (pendingPhoto && URL.createObjectURL(pendingPhoto)) ? (
                                            <img
                                                src={editingProduct?.foto_url
                                                    ? (editingProduct.foto_url.startsWith('http') ? editingProduct.foto_url : `${API_URL}${editingProduct.foto_url}`)
                                                    : URL.createObjectURL(pendingPhoto)}
                                                alt={editingProduct?.nombre || 'Vista previa'}
                                                className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                            />
                                        ) : (
                                            <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">
                                                Sin imagen
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-1">
                                            <input
                                                ref={imageInputRef}
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,image/gif"
                                                style={{ display: 'none' }}
                                                onChange={e => {
                                                    if (editingProduct) {
                                                        handlePhotoUpload(e, editingProduct.id);
                                                    } else {
                                                        setPendingPhoto(e.target.files[0] || null);
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => imageInputRef.current?.click()}
                                                className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 text-sm font-medium transition-colors cursor-pointer"
                                            >
                                                <ImagePlus size={18} /> {editingProduct?.foto_url || pendingPhoto ? 'Cambiar imagen' : 'Subir imagen'}
                                            </button>
                                            {pendingPhoto && !editingProduct && (
                                                <button type="button" onClick={() => setPendingPhoto(null)} className="text-xs text-red-600 hover:underline">
                                                    Quitar imagen
                                                </button>
                                            )}
                                            <span className="text-xs text-gray-500">JPEG, PNG, WebP o GIF (máx 2MB)</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!editingProduct && !pendingPhoto && (
                                <div className="form-group full-width">
                                    <label>Imagen del producto (opcional)</label>
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-600 file:font-medium hover:file:bg-indigo-100"
                                        onChange={e => setPendingPhoto(e.target.files[0] || null)}
                                    />
                                    <span className="text-xs text-gray-500">Puede agregar imagen al crear o después editando</span>
                                </div>
                            )}

                            {!editingProduct && (
                                <div className="form-group full-width">
                                    <label>Categoría / Subcategoría</label>
                                    <select
                                        value={formData.sub_categoria_id || ''}
                                        onChange={e => setFormData({ ...formData, sub_categoria_id: e.target.value })}
                                        required
                                    >
                                        <option value="">Seleccione Subcategoría</option>
                                        {categories.map(cat => (
                                            <optgroup key={cat.id} label={cat.nombre}>
                                                {cat.subcategories?.map(sub => (
                                                    <option key={sub.id} value={sub.id}>
                                                        {sub.nombre}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="form-actions">
                                <button type="submit" className="btn-primary" style={{ width: '100%' }}>
                                    {editingProduct ? 'Actualizar Producto' : 'Crear Producto'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
